/**
 * Lista delle risposte rapide aperta digitando "/" nel campo di scrittura.
 * Frecce per scegliere, Invio o Tab per inserire, Esc per chiudere (gestiti in MessageInput).
 */

import { memo } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import type { TemplateLike } from './quickReplies';

interface QuickReplyMenuProps {
  items: TemplateLike[];
  activeIndex: number;
  loading: boolean;
  query: string;
  onPick: (t: TemplateLike) => void;
  onHover: (index: number) => void;
}

export const QuickReplyMenu = memo(function QuickReplyMenu({ items, activeIndex, loading, query, onPick, onHover }: QuickReplyMenuProps) {
  return (
    <div
      className="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-[#252525] border border-[rgba(55,53,47,0.09)] dark:border-white/[0.06] rounded-lg shadow-lg overflow-hidden z-50"
      role="listbox"
      aria-label="Risposte rapide"
    >
      <div className="px-3 py-1.5 text-[10px] font-semibold text-[#A4A4A0] uppercase tracking-wider border-b border-[rgba(55,53,47,0.06)] dark:border-white/[0.06]">
        Risposte rapide{query ? `: "${query}"` : ''}
      </div>
      {loading && items.length === 0 && (
        <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-[#787774]" /></div>
      )}
      {!loading && items.length === 0 && (
        <p className="px-3 py-3 text-xs text-[#787774]">
          Nessun modello trovato. Puoi crearne uno dal pulsante dei modelli nella barra sopra il campo.
        </p>
      )}
      <div className="max-h-60 overflow-y-auto">
        {items.map((t, i) => (
          <button
            key={t.id}
            type="button"
            role="option"
            aria-selected={i === activeIndex}
            onMouseDown={(e) => { e.preventDefault(); onPick(t); }}
            onMouseEnter={() => onHover(i)}
            className={`w-full flex items-start gap-2 px-3 py-2 text-left transition-colors ${
              i === activeIndex ? 'bg-[rgba(55,53,47,0.06)] dark:bg-white/[0.06]' : 'hover:bg-[rgba(55,53,47,0.03)] dark:hover:bg-white/[0.03]'
            }`}
          >
            <FileText className="w-3.5 h-3.5 text-[#2383E2] flex-shrink-0 mt-0.5" />
            <span className="min-w-0">
              <span className="block text-xs font-medium text-[#37352F] dark:text-white truncate">{t.name}</span>
              <span className="block text-[11px] text-[#787774] truncate">{t.content}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
});
